import { useCustomizationLocales } from '@lib/customization/useCustomizationLocales';
import { useLocalization } from '@lib/localization';
import { ListSubheader, MenuItem, TextField, Typography } from '@mui/material';
import { startOptionStyles } from '@pages/ReportDesigns.styles';
import { resolveLabel } from '@store/api/poiTypeEndpoints';
import {
  ownerLabel, useGetReportDesignPresetsQuery, type CreateReportDesignInput, type ReportDesignOwner,
  type ReportDesignSummary,
} from '@store/api/reportDesignEndpoints';
import { useEffect, useRef, useState } from 'react';
import DesignDialog from './DesignDialog';
import { DescriptionField, NameField, OwnerField } from './DesignFields';

interface Props {
  open: boolean;
  /** Where the reader may create a design. Never empty while the dialog is open. */
  owners: ReportDesignOwner[];
  /** The designs the reader can read: any of them may be copied in place of a preset. */
  designs: ReportDesignSummary[];
  saving: boolean;
  onClose: () => void;
  onCreate: (input: CreateReportDesignInput) => void;
}

interface Draft {
  name: string;
  description: string;
  owner: ReportDesignOwner | null;
  /** The value of the start that was picked. Empty until one is, and the first on offer stands. */
  start: string;
}

/** Something a new design can start from, as the picker lists it. */
interface StartOption {
  value: string;
  from: CreateReportDesignInput['from'];
  name: string;
  /** What tells it from the others: a preset's description, a design's owner. */
  detail: string;
}

const draftFor = (owners: ReportDesignOwner[]): Draft => ({
  name: '',
  description: '',
  owner: owners[0] ?? null,
  start: '',
});

const startItem = (option: StartOption) => (
  <MenuItem key={option.value} value={option.value} sx={startOptionStyles}>
    <Typography variant="body2">{option.name}</Typography>
    {option.detail && <Typography variant="caption" color="text.secondary">{option.detail}</Typography>}
  </MenuItem>
);

/**
 * Starts a design. There is no empty one to start from: a design begins as a preset or as a
 * copy of another, and is made its own from there.
 */
const NewDesignDialog: React.FC<Props> = ({ open, owners, designs, saving, onClose, onCreate }) => {
  const { t } = useLocalization();
  const locales = useCustomizationLocales();

  // Asked for when the dialog first opens, not with the page: only starting a design needs them.
  const { data: presets = [], isLoading: isLoadingPresets } = useGetReportDesignPresetsQuery(undefined, {
    skip: !open,
  });

  const [draft, setDraft] = useState(() => draftFor(owners));

  // Emptied once each time the dialog opens, and never while it is open: what the reader
  // typed is not thrown away because the list of owners changed under it.
  const emptied = useRef(false);

  useEffect(() => {
    if (!open) {
      emptied.current = false;

      return;
    }

    if (emptied.current) return;

    emptied.current = true;
    setDraft(draftFor(owners));
  }, [open, owners]);

  const presetStarts: StartOption[] = presets.map((preset) => ({
    value: `preset:${preset.key}`,
    from: { preset: preset.key },
    name: resolveLabel(preset.name, locales),
    detail: resolveLabel(preset.description, locales),
  }));

  // Names are not unique across owners, so the owner is what tells two designs apart here.
  const designStarts: StartOption[] = designs.map((design) => ({
    value: `design:${design._id}`,
    from: { designId: design._id },
    name: design.name,
    detail: ownerLabel(design),
  }));

  const starts = [...presetStarts, ...designStarts];

  // Nothing stands in for the choice while the presets are on their way: the first design
  // would, and would then give way to the first preset under the reader's eyes.
  const start = starts.find((option) => option.value === draft.start) ?? (isLoadingPresets ? undefined : starts[0]);

  const submit = () => {
    if (!draft.owner || !start) return;

    onCreate({
      teamId: draft.owner.teamId,
      name: draft.name.trim(),
      description: draft.description.trim() || null,
      from: start.from,
    });
  };

  return (
    <DesignDialog
      open={open}
      title={t('ReportDesign_New')}
      submitLabel={t('ReportDesign_Create')}
      canSubmit={draft.name.trim() !== '' && draft.owner !== null && start !== undefined}
      saving={saving}
      onClose={onClose}
      onSubmit={submit}
    >
      <NameField
        value={draft.name}
        onChange={(name) => setDraft((current) => ({ ...current, name }))}
        disabled={saving}
      />

      <DescriptionField
        value={draft.description}
        onChange={(description) => setDraft((current) => ({ ...current, description }))}
        disabled={saving}
      />

      <OwnerField
        owners={owners}
        value={draft.owner}
        onChange={(owner) => setDraft((current) => ({ ...current, owner }))}
        disabled={saving}
      />

      <TextField
        select
        fullWidth
        size="small"
        label={t('ReportDesign_StartFrom')}
        value={start?.value ?? ''}
        onChange={(event) => setDraft((current) => ({ ...current, start: event.target.value }))}
        // An entry is two lines in the menu; the closed field shows the name alone.
        slotProps={{ select: { renderValue: () => start?.name } }}
        disabled={saving}
      >
        {presetStarts.length > 0 && <ListSubheader>{t('ReportDesign_Presets')}</ListSubheader>}
        {presetStarts.map(startItem)}
        {designStarts.length > 0 && <ListSubheader>{t('ReportDesign_ExistingDesigns')}</ListSubheader>}
        {designStarts.map(startItem)}
      </TextField>
    </DesignDialog>
  );
};

export default NewDesignDialog;
